/**
 * FilterPanel Component
 * Container that groups all basic filter components
 * Manages layout and provides "Clear All" functionality
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 2
 */

import * as React from 'react';
import { Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { SearchBar } from './SearchBar';
import { StatusFilter } from './StatusFilter';
import { CustomerTypeFilter } from './CustomerTypeFilter';
import { DateRangeFilters } from './DateRangeFilters';
import { AmountRangeFilter } from './AmountRangeFilter';
import { LocationFilters } from './LocationFilters';
import { useOrderFilters } from '../hooks/useOrderFilters';
import { useAvailableStatuses } from '../hooks/useAvailableStatuses';

// ============================================
// INTERFACE
// ============================================

export interface FilterPanelProps {
  filters?: any;
  onFiltersChange?: (filters: any) => void;
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export const FilterPanel = React.memo(function FilterPanel({
  filters: propsFilters,
  onFiltersChange,
  className,
}: FilterPanelProps) {
  // Use hook only if props are not provided
  const hookFilters = useOrderFilters();

  // Prefer props over hook values
  const filters = propsFilters !== undefined ? propsFilters : hookFilters.filters;
  const updateFilters = onFiltersChange || hookFilters.updateFilters;

  // Calculate hasActiveFilters based on actual filters being used
  const hasActiveFilters =
    filters.searchTerm !== '' ||
    filters.statuses.length > 0 ||
    filters.orderDateRange.from !== null ||
    filters.orderDateRange.to !== null ||
    filters.expectedDeliveryDateRange.from !== null ||
    filters.expectedDeliveryDateRange.to !== null ||
    filters.actualDeliveryDateRange.from !== null ||
    filters.actualDeliveryDateRange.to !== null ||
    filters.amountRange.min !== null ||
    filters.amountRange.max !== null ||
    filters.customerType !== 'ALL' ||
    filters.location.state !== null ||
    filters.location.city !== null;

  // Mobile collapse state
  const [isExpanded, setIsExpanded] = React.useState(true);

  // Toggle mobile expansion
  const handleToggleExpanded = React.useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Fetch available statuses based on current filters
  const { availableStatuses, isLoading: isLoadingStatuses } = useAvailableStatuses(
    filters.searchTerm,
    filters.customerType,
    filters.location
  );

  // Handle search change
  const handleSearchChange = React.useCallback(
    (term: string) => {
      console.log('[FilterPanel] Search term changed:', term);
      updateFilters({ searchTerm: term });
    },
    [updateFilters]
  );

  // Handle status change
  const handleStatusChange = React.useCallback(
    (statuses: typeof filters.statuses) => {
      updateFilters({ statuses });
    },
    [updateFilters]
  );

  // Handle customer type change
  const handleCustomerTypeChange = React.useCallback(
    (customerType: typeof filters.customerType) => {
      updateFilters({ customerType });
    },
    [updateFilters]
  );

  // Handle date range change
  const handleDateRangeChange = React.useCallback(
    (from: string | null, to: string | null) => {
      updateFilters({ orderDateRange: { from, to } });
    },
    [updateFilters]
  );

  // Handle amount range change
  const handleAmountRangeChange = React.useCallback(
    (min: number | null, max: number | null) => {
      updateFilters({ amountRange: { min, max } });
    },
    [updateFilters]
  );

  // Handle location change
  const handleLocationChange = React.useCallback(
    (city: string | null, state: string | null) => {
      updateFilters({ location: { city, state } });
    },
    [updateFilters]
  );

  // Calculate active filter count (excluding default values)
  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filters.searchTerm) count++;
    if (filters.statuses.length > 0) count++;
    if (filters.customerType !== 'ALL') count++;
    if (filters.orderDateRange.from || filters.orderDateRange.to) count++;
    if (filters.amountRange.min !== null || filters.amountRange.max !== null) count++;
    if (filters.location.city || filters.location.state) count++;
    return count;
  }, [
    filters.searchTerm,
    filters.statuses.length,
    filters.customerType,
    filters.orderDateRange.from,
    filters.orderDateRange.to,
    filters.amountRange.min,
    filters.amountRange.max,
    filters.location.city,
    filters.location.state,
  ]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header with Clear All Button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Filters</h2>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="h-6 px-2 text-xs font-medium">
              {activeFilterCount} active
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Mobile Toggle Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleToggleExpanded}
            className="h-8 gap-1.5 text-xs md:hidden"
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                Hide
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                Show
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filter Content */}
      <div
        className={cn(
          'space-y-4 transition-all duration-200',
          !isExpanded && 'hidden md:block'
        )}
        aria-hidden={!isExpanded}
      >
        {/* Search Bar - Full Width */}
        <div className="w-full">
          <SearchBar
            searchTerm={filters.searchTerm}
            onSearchChange={handleSearchChange}
          />
        </div>

        {/* Compact Filter Grid - Single Row Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Customer Type Filter - First */}
          <div className="lg:col-span-1">
            <CustomerTypeFilter
              selectedCustomerType={filters.customerType}
              onCustomerTypeChange={handleCustomerTypeChange}
            />
          </div>

          {/* Status Filter - Dropdown Style */}
          <div className="lg:col-span-1">
            <StatusFilter
              selectedStatuses={filters.statuses}
              onStatusChange={handleStatusChange}
              availableStatuses={availableStatuses}
              isLoadingStatuses={isLoadingStatuses}
            />
          </div>

          {/* Date Range Filter - Calendar Picker */}
          <div className="lg:col-span-1">
            <DateRangeFilters
              dateRanges={filters.orderDateRange}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>

          {/* Amount Range Filter */}
          <div className="lg:col-span-1">
            <AmountRangeFilter
              minAmount={filters.amountRange.min}
              maxAmount={filters.amountRange.max}
              onAmountRangeChange={handleAmountRangeChange}
            />
          </div>

          {/* Location Filters */}
          <div className="lg:col-span-1">
            <LocationFilters
              selectedCity={filters.location.city}
              selectedState={filters.location.state}
              onLocationChange={handleLocationChange}
            />
          </div>
        </div>
      </div>

      {/* Accessibility announcements */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {hasActiveFilters
          ? `${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'} applied`
          : 'No filters applied'}
      </div>
    </div>
  );
});
